
import React from 'react';
import { Thermometer, Wrench, AlertTriangle, Cpu, Server, Radio, Activity, Battery, Zap } from 'lucide-react';
import { Clock, BrainCircuit, ActivitySquare, DollarSign, Box, Timer } from 'lucide-react';
import { BarChart3, TrendingDown, Gauge } from 'lucide-react';

export const maintenanceAlerts = [
  {
    title: "Chiller 3 Service Required",
    status: "Alert",
    issue: "Compressor vibration exceeding threshold",
    icon: React.createElement(Thermometer, { className: "h-4 w-4 text-white" }),
    bgClass: "bg-red-500",
    deviceId: "chiller-03",
    deviceType: "chiller",
    reportDate: "2024-06-01",
    aiPrediction: "Compressor failure expected in 14 days if not serviced",
    confidence: 91,
    assetName: "Chiller 3",
    assetType: "Chiller",
    facility: "Plant 2",
    location: "Plant 2 – Zone B-23",
    estimatedImpact: "Chiller COP drop by 8%, potential loss: $650/month",
    nextInspection: "10-Apr-2024"
  },
  {
    title: "Transformer T-12 Inspection",
    status: "Alert",
    issue: "Oil temperature rising consistently",
    icon: React.createElement(Zap, { className: "h-4 w-4 text-white" }),
    bgClass: "bg-red-500",
    deviceId: "transformer-12",
    deviceType: "transformer",
    reportDate: "2024-06-02",
    aiPrediction: "Potential overheating risk within 7 days",
    confidence: 89,
    assetName: "Transformer T-12",
    assetType: "Transformer",
    facility: "Plant 1",
    location: "Plant 1 – Power Room",
    estimatedImpact: "Risk of service interruption to manufacturing line",
    nextInspection: "15-Apr-2024"
  },
  {
    title: "HVAC Unit Maintenance",
    status: "Warning",
    issue: "Filter replacement needed",
    icon: React.createElement(Activity, { className: "h-4 w-4 text-white" }),
    bgClass: "bg-amber-500",
    deviceId: "hvac-22",
    deviceType: "hvac",
    reportDate: "2024-05-28",
    aiPrediction: "Efficiency decrease of 12% expected in 30 days",
    confidence: 86,
    assetName: "HVAC Unit F-22",
    assetType: "HVAC",
    facility: "Headquarters",
    location: "Headquarters – Floor 3",
    estimatedImpact: "Energy waste: $145/month",
    nextInspection: "18-Apr-2024"
  },
  {
    title: "Smart Meter Battery Check",
    status: "Warning",
    issue: "Battery degradation detected",
    icon: React.createElement(Battery, { className: "h-4 w-4 text-white" }),
    bgClass: "bg-amber-500",
    deviceId: "sm-a1-45",
    deviceType: "smartMeter",
    reportDate: "2024-05-25",
    aiPrediction: "Battery replacement needed within 60 days",
    confidence: 82,
    assetName: "Smart Meter A1-45",
    assetType: "Smart Meter",
    facility: "Plant 2",
    location: "Plant 2 – Building A",
    estimatedImpact: "Potential communication failure risk",
    nextInspection: "20-Apr-2024"
  },
  {
    title: "Load Controller Inspection",
    status: "Warning",
    issue: "Minor timing drifts detected",
    icon: React.createElement(Cpu, { className: "h-4 w-4 text-white" }),
    bgClass: "bg-amber-500",
    deviceId: "lc-27",
    deviceType: "loadController",
    reportDate: "2024-05-30",
    aiPrediction: "Timing drift will exceed threshold in ~45 days",
    confidence: 78,
    assetName: "Load Controller LC-27",
    assetType: "Load Controller",
    facility: "Data Center",
    location: "Data Center – Zone C",
    estimatedImpact: "Potential loss: $312/month in optimization",
    nextInspection: "22-Apr-2024"
  },
  {
    title: "IoT Gateway Health Check",
    status: "Good",
    nextInspection: "28-Apr-2024",
    icon: React.createElement(Radio, { className: "h-4 w-4 text-white" }),
    bgClass: "bg-green-500",
    deviceId: "gateway-05",
    deviceType: "gateway",
    reportDate: "2024-06-03",
    assetName: "IoT Gateway G-05",
    assetType: "IoT Gateway",
    facility: "Plant 1",
    location: "Plant 1 – South Wing"
  }
];

export const maintenanceParameters = [
  {
    title: "Automated Scheduling",
    description: "Schedule maintenance tasks based on time, usage, or predictive analysis",
    icon: React.createElement(Clock, { className: "h-5 w-5 text-energy-blue" }),
    bgClass: "bg-energy-blue/10",
    status: "Active",
    lastUpdated: "2 days ago"
  },
  {
    title: "AI Predictive Analytics",
    description: "Early warning alerts and fault detection using machine learning",
    icon: React.createElement(BrainCircuit, { className: "h-5 w-5 text-energy-teal" }),
    bgClass: "bg-energy-teal/10",
    status: "Active",
    lastUpdated: "Today"
  },
  {
    title: "IoT Sensor Integration",
    description: "Real-time condition monitoring via connected sensors",
    icon: React.createElement(ActivitySquare, { className: "h-5 w-5 text-energy-green" }),
    bgClass: "bg-energy-green/10",
    status: "Active",
    lastUpdated: "1 hour ago"
  },
  {
    title: "Maintenance Cost Tracking",
    description: "Budget allocation and expense monitoring",
    icon: React.createElement(DollarSign, { className: "h-5 w-5 text-energy-red" }),
    bgClass: "bg-energy-red/10",
    status: "Active",
    lastUpdated: "Yesterday"
  },
  {
    title: "Parts Inventory",
    description: "Tracking consumables and replacement parts",
    icon: React.createElement(Box, { className: "h-5 w-5 text-amber-500" }),
    bgClass: "bg-amber-500/10",
    status: "Active",
    lastUpdated: "3 days ago"
  },
  {
    title: "Downtime Analysis",
    description: "Mitigation planning for critical assets",
    icon: React.createElement(Timer, { className: "h-5 w-5 text-energy-gray" }),
    bgClass: "bg-energy-gray/10",
    status: "Active",
    lastUpdated: "Yesterday"
  }
];

export const aiPredictions = [
  {
    title: "Failure Prediction",
    description: "Predicts potential equipment failures before they occur by analyzing historical data patterns",
    icon: React.createElement(BarChart3, { className: "h-5 w-5 text-energy-blue" }),
    accuracy: "94%",
    impact: "High",
    recommendedAction: "Schedule preventive maintenance within the next 14 days"
  },
  {
    title: "Optimal Maintenance Timing",
    description: "Determines the ideal time for maintenance based on equipment condition and operational demands",
    icon: React.createElement(Clock, { className: "h-5 w-5 text-energy-green" }),
    accuracy: "89%",
    impact: "Medium",
    recommendedAction: "Adjust maintenance schedule to coincide with low-usage periods"
  },
  {
    title: "Component Lifespan Estimation",
    description: "Forecasts remaining useful life of critical components based on usage patterns and environmental factors",
    icon: React.createElement(Cpu, { className: "h-5 w-5 text-energy-teal" }),
    accuracy: "91%",
    impact: "High",
    recommendedAction: "Order replacement parts for components approaching end-of-life"
  },
  {
    title: "Anomaly Detection",
    description: "Identifies unusual patterns in energy consumption or equipment behavior that indicate potential issues",
    icon: React.createElement(AlertTriangle, { className: "h-5 w-5 text-energy-red" }),
    accuracy: "97%",
    impact: "High",
    recommendedAction: "Investigate unusual patterns identified in sensor readings"
  },
  
  {
    title: "Battery Health Analysis",
    description: "Battery is showing signs of degradation with reduced capacity and charge retention",
    icon: React.createElement(Battery, { className: "h-5 w-5 text-amber-500" }),
    accuracy: "96%",
    impact: "Medium",
    deviceId: "gw-3045",
    alert: "Battery degradation detected",
    recommendedAction: "Replace battery within 30 days to prevent potential connectivity issues"
  },
  {
    title: "Transformer Health Prediction",
    description: "Abnormal voltage patterns detected suggest early stage transformer wear",
    icon: React.createElement(Zap, { className: "h-5 w-5 text-energy-red" }),
    accuracy: "87%",
    impact: "High",
    deviceId: "sm-10045",
    alert: "Unusual power fluctuation pattern",
    recommendedAction: "Schedule diagnostic testing and prepare for possible transformer replacement"
  },
  {
    title: "Performance Optimization",
    description: "Current performance degradation pattern indicates component wear before critical failure",
    icon: React.createElement(TrendingDown, { className: "h-5 w-5 text-amber-500" }),
    accuracy: "92%",
    impact: "Medium",
    deviceId: "vr-2034",
    alert: "Performance degradation",
    recommendedAction: "Perform preventative maintenance and component inspection within 2 weeks"
  },
  {
    title: "Calibration Analysis",
    description: "Flow sensor readings show increasing deviation from expected values, indicating calibration drift",
    icon: React.createElement(Gauge, { className: "h-5 w-5 text-amber-500" }),
    accuracy: "89%",
    impact: "Medium",
    deviceId: "gas-5023",
    alert: "Calibration drift detected",
    recommendedAction: "Schedule recalibration within 3 weeks to maintain measurement accuracy"
  },
  {
    title: "Pressure System Integrity",
    description: "Pressure fluctuations detected in the distribution system suggest a potential leak or valve issue",
    icon: React.createElement(AlertTriangle, { className: "h-5 w-5 text-energy-red" }),
    accuracy: "94%",
    impact: "High",
    deviceId: "gas-6078",
    alert: "Pressure irregularity detected",
    recommendedAction: "Dispatch inspection team to segment B-23 and prepare repair equipment"
  }
];
